import * as React from 'react';
import { useBooleanState } from 'owa-react-hooks/lib/useBooleanState';
import { useInnerFocusTracker } from 'owa-react-hooks/lib/useInnerFocusTracker';
import { usePointerEvents } from 'owa-react-hooks/lib/usePointerEvents';
import { useKeydownHandler } from 'owa-hotkeys';
import { owaDate, formatDate } from 'owa-datetime';
import { observableNow } from 'owa-observable-datetime';
import { getTimeFormat } from 'owa-datetime-store';
import { getTimeZoneShortDisplayName } from 'owa-timezone';
import { usePatchPartialPayload, setAnnouncement } from 'owa-timestream-asset-sdk';
import { useClockPayload } from '../hooks/useClockPayload';
import type { TimeZoneInfo } from '../schema';
import loc from 'owa-localize';
import { timeZoneRemoved, timeZoneShortName } from '../clockStrings.locstring.json';

export interface TimeZoneViewProps {
    timeZoneInfo: TimeZoneInfo;
    index: number;
}

export default function useTimeZoneViewViewState({ timeZoneInfo, index }: TimeZoneViewProps) {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const { hasFocusState, onFocusCapture, onBlurCapture } = useInnerFocusTracker(containerRef);
    const [shouldShowRemove, showRemove, hideRemove] = useBooleanState(false);
    const usingPointerEvents = usePointerEvents();
    const patchPartialPayload = usePatchPartialPayload();
    const payload = useClockPayload();

    const { timeZoneName, timeZoneId, shortName, displayName, city } = timeZoneInfo;
    const formattedDate = formatDate(owaDate(timeZoneId, observableNow()), getTimeFormat());

    const removeTimeZone = React.useCallback(() => {
        const timeZones = [...payload.timeZones];
        timeZones.splice(index, 1);
        patchPartialPayload({ timeZones: timeZones });
        setAnnouncement(loc(timeZoneRemoved, timeZoneInfo.city));
    }, [payload.timeZones, timeZoneInfo.city, index]);

    useKeydownHandler(containerRef, 'del', removeTimeZone, {
        stopPropagation: true,
        preventDefault: true,
    });

    return {
        numTimeZones: payload.timeZones.length,
        containerAttributes: {
            ref: containerRef,
            onFocusCapture,
            onBlurCapture,
            onMouseEnter: usingPointerEvents ? undefined : showRemove,
            onMouseLeave: usingPointerEvents ? undefined : hideRemove,
            onPointerEnter: usingPointerEvents ? showRemove : undefined,
            onPointerLeave: usingPointerEvents ? hideRemove : undefined,
        },
        displayName,
        city,
        timeZoneShortName: loc(
            timeZoneShortName,
            shortName,
            getTimeZoneShortDisplayName(timeZoneId)
        ),
        timeZoneName,
        formattedDate,
        shouldShowRemove: shouldShowRemove || hasFocusState,
        removeButtonProps: {
            onClick: removeTimeZone,
        },
    };
}
